import { useRef, useState } from "react";
import {
  Check,
  Code2,
  Copy,
  Download,
  ExternalLink,
  Link2,
  Loader2,
  Package,
  QrCode,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Button, Input, Textarea } from "@sportscanner/ui";
import { apiGet } from "@/lib/api";
import { type MeClub, useMe } from "@/lib/me";

const SITE_URL = (
  import.meta.env.VITE_SITE_URL || "http://localhost:3000"
).replace(/\/$/, "");

// PNG export resolution. The on-screen QR is CSS-scaled down from this so the
// downloaded image is sharp enough to print on a poster.
const QR_EXPORT_SIZE = 1024;
const QR_DISPLAY_PX = 192;

interface Props {
  clubId: string;
}

export default function IntegrationPanels({ clubId }: Props) {
  const { t } = useTranslation();
  const { club: meClub } = useMe();

  // SUPERADMIN can scope to a different club — fall back to a fetch in that
  // case. MANAGER/STAFF always see their own club so we reuse the /me payload.
  const useMeSlug = meClub?.id === clubId;
  const { data: fetchedClub, isLoading } = useQuery({
    queryKey: ["admin", "clubs", clubId],
    queryFn: async () => {
      const data = await apiGet<{ club: MeClub }>(`/api/admin/clubs/${clubId}`);
      return data.club;
    },
    enabled: !useMeSlug,
  });

  const club = useMeSlug ? meClub : fetchedClub;

  if (!club && isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }
  if (!club) return null;

  const publicUrl = `${SITE_URL}/${club.slug}`;
  const embedUrl = `${publicUrl}?embed=true`;
  const iframeCode = buildIframeSnippet(embedUrl);
  const webComponentCode = buildWebComponentSnippet(club.slug);

  return (
    <div className="space-y-6">
      <PublicLinkCard publicUrl={publicUrl} slug={club.slug} />
      <IframeEmbedCard
        iframeCode={iframeCode}
        embedUrl={embedUrl}
        publicUrl={publicUrl}
      />
      <WebComponentCard code={webComponentCode} />
      <p className="text-xs text-neutral-400 text-center">
        {t("integration.support")}
      </p>
    </div>
  );

  function buildIframeSnippet(src: string): string {
    return `<iframe
  src="${src}"
  title="${club!.name}"
  width="100%"
  height="900"
  style="border: 0; max-width: 640px; display: block; margin: 0 auto;"
  loading="lazy"
></iframe>`;
  }

  function buildWebComponentSnippet(slug: string): string {
    return `<script type="module" src="https://unpkg.com/@sportscanner/booking-widget"></script>
<sportscanner-booking slug="${slug}"></sportscanner-booking>`;
  }
}

function SectionCard({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6">
      <header className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
          {icon}
        </div>
        <div>
          <h2 className="font-heading font-semibold text-neutral-800">
            {title}
          </h2>
          {description && (
            <p className="text-sm text-neutral-500 mt-0.5">{description}</p>
          )}
        </div>
      </header>
      {children}
    </section>
  );
}

function PublicLinkCard({
  publicUrl,
  slug,
}: {
  publicUrl: string;
  slug: string;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const qrRef = useRef<HTMLCanvasElement>(null);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success(t("integration.toast.linkCopied"));
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("integration.toast.copyError"));
    }
  }

  function handleDownloadQr() {
    const canvas = qrRef.current;
    if (!canvas) return;
    try {
      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `${slug}-qr.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(t("integration.toast.qrDownloaded"));
    } catch {
      toast.error(t("integration.toast.qrDownloadError"));
    }
  }

  return (
    <SectionCard
      icon={<Link2 className="w-4 h-4" />}
      title={t("integration.publicLink.title")}
      description={t("integration.publicLink.description")}
    >
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            readOnly
            value={publicUrl}
            onFocus={(e) => e.currentTarget.select()}
            className="font-mono text-sm flex-1"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCopy}
              className="flex-1 sm:flex-none"
            >
              {copied ? (
                <Check className="w-4 h-4 mr-2" />
              ) : (
                <Copy className="w-4 h-4 mr-2" />
              )}
              {copied
                ? t("integration.actions.copied")
                : t("integration.actions.copy")}
            </Button>
            <Button
              type="button"
              variant="outline"
              asChild
              className="flex-1 sm:flex-none"
            >
              <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                {t("integration.actions.open")}
              </a>
            </Button>
          </div>
        </div>

        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 sm:p-5 flex flex-col sm:flex-row items-center gap-5">
          <div
            className="rounded-lg bg-white p-3 border border-neutral-200 shrink-0"
            style={{ width: QR_DISPLAY_PX + 24, height: QR_DISPLAY_PX + 24 }}
          >
            <QRCodeCanvas
              value={publicUrl}
              size={QR_EXPORT_SIZE}
              level="M"
              marginSize={2}
              style={{
                width: QR_DISPLAY_PX,
                height: QR_DISPLAY_PX,
              }}
              ref={qrRef}
            />
          </div>
          <div className="flex-1 text-center sm:text-left space-y-2">
            <div className="flex items-center justify-center sm:justify-start gap-2 text-sm font-medium text-neutral-700">
              <QrCode className="w-4 h-4 text-primary-500" />
              {t("integration.qr.heading")}
            </div>
            <p className="text-sm text-neutral-500 leading-relaxed">
              {t("integration.qr.description")}
            </p>
            <div className="pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={handleDownloadQr}
              >
                <Download className="w-4 h-4 mr-2" />
                {t("integration.qr.download")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

function IframeEmbedCard({
  iframeCode,
  embedUrl,
  publicUrl,
}: {
  iframeCode: string;
  embedUrl: string;
  publicUrl: string;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(iframeCode);
      setCopied(true);
      toast.success(t("integration.toast.snippetCopied"));
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("integration.toast.copyError"));
    }
  }

  return (
    <SectionCard
      icon={<Code2 className="w-4 h-4" />}
      title={t("integration.iframe.title")}
      description={t("integration.iframe.description")}
    >
      <div className="space-y-4">
        <Textarea
          readOnly
          value={iframeCode}
          rows={7}
          onFocus={(e) => e.currentTarget.select()}
          className="font-mono text-xs bg-neutral-50"
        />
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={handleCopy}>
            {copied ? (
              <Check className="w-4 h-4 mr-2" />
            ) : (
              <Copy className="w-4 h-4 mr-2" />
            )}
            {copied
              ? t("integration.actions.copied")
              : t("integration.iframe.copySnippet")}
          </Button>
          <Button type="button" variant="outline" asChild>
            <a href={embedUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-4 h-4 mr-2" />
              {t("integration.iframe.preview")}
            </a>
          </Button>
        </div>
        <div className="rounded-lg bg-primary-50/60 border border-primary-100 px-4 py-3">
          <p className="text-xs text-neutral-600 leading-relaxed">
            <span className="font-semibold text-neutral-800">
              {t("integration.iframe.tipLabel")}
            </span>{" "}
            {t("integration.iframe.tipBody", { url: publicUrl })}
          </p>
        </div>
      </div>
    </SectionCard>
  );
}

function WebComponentCard({ code }: { code: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(t("integration.toast.snippetCopied"));
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("integration.toast.copyError"));
    }
  }

  return (
    <SectionCard
      icon={<Package className="w-4 h-4" />}
      title={t("integration.webComponent.title")}
      description={t("integration.webComponent.description")}
    >
      <div className="space-y-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 text-xs font-medium">
          {t("integration.webComponent.badge")}
        </span>
        <Textarea
          readOnly
          value={code}
          rows={3}
          onFocus={(e) => e.currentTarget.select()}
          className="font-mono text-xs bg-neutral-50"
        />
        <div>
          <Button type="button" variant="outline" onClick={handleCopy}>
            {copied ? (
              <Check className="w-4 h-4 mr-2" />
            ) : (
              <Copy className="w-4 h-4 mr-2" />
            )}
            {copied
              ? t("integration.actions.copied")
              : t("integration.actions.copy")}
          </Button>
        </div>
        <p className="text-xs text-neutral-500 leading-relaxed">
          {t("integration.webComponent.note")}
        </p>
      </div>
    </SectionCard>
  );
}
